import { observable, computed, runInAction } from 'mobx';
import { PlayerModel } from '../player/model';
import { defaultDescription, defaultTitle } from '../../lib/ssr';
import { createSimpleSchema, list, object, serializable, update } from 'serializr';
import { OnboardingModel } from '../onboarding/model';
import { AuthModel } from '../auth/model';
import { StorageModel } from '../storage/model';
import { RouterModel } from '../router/model';
import { ContentModel } from '../content/model';
import { ModalModel } from '../modal/model';
import { UIBreadcrumb, UIOpenGraph } from '../../types/ui';
import {constantTabletBreakpoint} from '../../theme/constant';

const BreadcrumbsSchema = createSimpleSchema({
  label: true,
  path: true
});

const OpenGraphSchema = createSimpleSchema({
  title: true,
  description: true,
  type: true,
  image: true,
  url: true,
});

export class UIModel {
  constructor(
    private player: PlayerModel,
    private onboarding: OnboardingModel,
    private auth: AuthModel,
    private storage: StorageModel,
    private router: RouterModel,
    private content: ContentModel,
    private modal: ModalModel,
  ) {
    this.loadInitial();
  }

  loadInitial = () => {
    if (!this.isServer) {
      const {initialUIModel} = window as any;
      if (initialUIModel) runInAction(() => {
        update(this, initialUIModel);
      });
    }
  };

  @serializable
  @observable
  description: string = defaultTitle;

  @serializable
  @observable
  title: string = defaultDescription;

  @serializable(list(object(BreadcrumbsSchema)))
  @observable
  breadcrumbs: Array<UIBreadcrumb> = [];

  @computed
  get showBreadcrumbs(): boolean {
    if (!this.breadcrumbs || this.breadcrumbs.length === 0)
      return false;

    if (this.breadcrumbs.length > 1)
      return true;

    const root = this.breadcrumbs[0];

    switch (true) {
      case (root.path === '/'):
      case (root.path === '/browse'):
      case (root.path === '/search'):
      case (root.path.startsWith('/account')):
        return false;

      default:
        return true;
    }
  }

  @observable
  authWindow: boolean = false;
  
  @serializable(object(OpenGraphSchema))
  @observable
  opengraph: UIOpenGraph = {};

  @observable
  isServer: boolean = !!process.env.SERVER;

  @observable
  imageLoadIndex: number = 0;

  @observable
  firstPaint: boolean = true;

  @observable
  resultsOverlay: boolean = false;

  @observable
  showBasketPopover: boolean = false;

  @observable
  showAccountPopover: boolean = false;

  @observable
  showSearchPopover: boolean = false;

  @serializable
  @observable
  showSearchDrawer: boolean = false;

  @observable
  showMenuDrawer: boolean = false;

  @observable
  showSignUpModal: boolean = false;

  @observable
  forceSolidHeader: boolean = false;

  @observable
  scrollingOverride: boolean = false;

  @observable
  showChrome: boolean = true;

  @computed
  get isAdvanced(): boolean {
    if (!this.router.location) return false;

    return this.router.location.pathname.startsWith('/search')
  }

  @computed
  get showPlayer(): boolean {
    return !!this.player.currentTrack
  };

  @computed
  get solidHeader(): boolean {
    return true;
  };

  @observable
  window: UIModelWindow = {
    height: 0,
    width: 0,
  };

  @computed
  get windowSizeHash() {
    return this.window.width;
  }

  @computed
  get isMobile() {
    return this.window.width < constantTabletBreakpoint;
  }

  @computed
  get showAnyModals(): boolean {
    return this.modal.showAny || this.showSignUpModal;
  }

  @computed
  get preventScrolling() {
    return this.showMenuDrawer && this.isMobile
      || this.showAnyModals
      || (this.showSearchDrawer && this.isMobile)
      || (this.auth.hasFetched && this.onboarding.showAny)
      || (this.isMobile && (this.showAccountPopover || this.showBasketPopover))
      || this.scrollingOverride;
  }

  @computed
  get hideHubspotChat() {
    return this.isMobile && (this.showSearchDrawer || this.showMenuDrawer || this.showBasketPopover || this.showAccountPopover)
  }

  @computed
  get showSignUpCTA() {
    return false;
    // return !this.isServer
    //   && this.auth.hasFetched
    //   && !this.auth.user
    //   && !(this.storage.getItem('hideSignUpCTA') === '1')
  }

  @computed
  get showJustAdded(): boolean {
    return this.showChrome && !/^\/checkout/.test(this.router.location.pathname);
  }

  @computed
  get cookieNotification() {
    return {
      show: this.content.cookieNotificationTitle && this.content.cookieNotificationContent && !(this.storage.getItem('hideCookieNotification') === '1'),
      title: this.content.cookieNotificationTitle,
      content: this.content.cookieNotificationContent
    }
  }
}

export interface UIModelWindow {
  height: number;
  width: number;
}